import { useLayoutEffect, useRef } from 'react';
import { formatMoney, formatNumber } from '../lib/format';
import {
  NODE_TITLES,
  layoutFlow,
  summarizeStep,
  type FlowBranch,
  type FlowItem,
} from '../lib/flowGraph';
import { TRIGGER_LABELS } from '../lib/automationLabels';
import type {
  AutomationStats,
  FlowDefinition,
  FlowStepNumbers,
  FlowStepStats,
  TriggerType,
} from '../types/api';

type Step = FlowDefinition['steps'][number];

const NO_NUMBERS: FlowStepNumbers = {
  entered: 0,
  sent: 0,
  opened: 0,
  clicked: 0,
  ordered: 0,
  revenue: 0,
};

interface Shared {
  steps: Step[];
  stepStats?: FlowStepStats | undefined;
  currency: string;
  onSelectStep?: ((stepId: string) => void) | undefined;
}

/** The numbers shown on a node, as short labelled pairs. Empty when the server sent no step numbers. */
function numberPairs(
  step: Step,
  stepStats: FlowStepStats | undefined,
  currency: string,
): [string, string][] {
  if (!stepStats) return [];
  // A step nobody has reached has no entry: it reads as zero once numbers exist at all.
  const n = stepStats.steps[step.id] ?? NO_NUMBERS;
  const pairs: [string, string][] = [['Entered', formatNumber(n.entered)]];
  if (step.type === 'send_template') {
    pairs.push(['Sent', formatNumber(n.sent)]);
    pairs.push(['Opened', formatNumber(n.opened)]);
    pairs.push(['Clicked', formatNumber(n.clicked)]);
    if (n.ordered > 0) {
      pairs.push(['Orders', formatNumber(n.ordered)]);
      pairs.push(['Revenue', formatMoney(n.revenue, currency)]);
    }
  }
  return pairs;
}

function Connector() {
  return <span aria-hidden="true" className="block w-px h-5 bg-border mx-auto" />;
}

function Node({
  step,
  index,
  stepStats,
  currency,
  onSelectStep,
}: Shared & {
  step: Step;
  index: number;
}) {
  const title = NODE_TITLES[step.type] ?? step.type;
  const summary = summarizeStep(step);
  const pairs = numberPairs(step, stepStats, currency);
  const label = `Step ${index + 1}, ${title}: ${summary}.${
    pairs.length > 0 ? ` ${pairs.map(([k, v]) => `${k} ${v}`).join(', ')}.` : ''
  } Open in the step list.`;
  return (
    <button
      type="button"
      aria-label={label}
      data-step-id={step.id}
      onClick={() => onSelectStep?.(step.id)}
      className="w-56 text-left rounded-xl border border-border bg-card px-3 py-2.5 hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
    >
      <span className="block text-xs text-muted font-medium">
        Step {index + 1} · {title}
      </span>
      <span className="block text-sm break-words mt-0.5">{summary}</span>
      {pairs.length > 0 ? (
        <span className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs text-muted font-mono">
          {pairs.map(([k, v]) => (
            <span key={k}>
              {k} {v}
            </span>
          ))}
        </span>
      ) : null}
    </button>
  );
}

function BranchColumn({
  branch,
  parentTitle,
  ...shared
}: Shared & { branch: FlowBranch; parentTitle: string }) {
  const { stepStats, steps } = shared;
  const runs = stepStats
    ? branch.pathKeys.reduce((sum, key) => sum + (stepStats.paths[key] ?? 0), 0)
    : null;
  const target = steps[branch.to];
  return (
    <li className="flex flex-col items-center">
      <span aria-hidden="true" className="block w-px h-3 bg-border" />
      <h4 className="m-0 text-xs font-semibold rounded-full bg-accent-soft text-link px-2.5 py-1">
        <span className="sr-only">{parentTitle}: </span>
        {branch.label}
        {runs !== null && branch.pathKeys.length > 0 ? ` · ${formatNumber(runs)} runs` : ''}
      </h4>
      {branch.items.length > 0 ? (
        <Sequence items={branch.items} {...shared} />
      ) : (
        <>
          <Connector />
          <span className="text-xs text-muted text-center w-56">
            {target
              ? `Goes straight to ${NODE_TITLES[target.type] ?? target.type}`
              : 'Ends the flow'}
          </span>
        </>
      )}
    </li>
  );
}

function Sequence({ items, ...shared }: Shared & { items: FlowItem[] }) {
  return (
    <ol className="m-0 p-0 list-none flex flex-col items-center">
      {items.map((item) => (
        <li key={item.step.id} className="flex flex-col items-center">
          <Connector />
          <Node step={item.step} index={item.index} {...shared} />
          {item.kind === 'fork' ? (
            <>
              <Connector />
              <ul
                aria-label={`Paths after step ${item.index + 1}`}
                className="m-0 p-0 list-none flex items-start gap-4 border-t border-border"
              >
                {item.branches.map((b) => (
                  <BranchColumn
                    key={`${b.label}:${b.to}`}
                    branch={b}
                    parentTitle={`Step ${item.index + 1}`}
                    {...shared}
                  />
                ))}
              </ul>
            </>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/**
 * Read-only tree of one flow, top to bottom: the trigger, then the steps, with
 * branches and A/B paths side by side. It is also a nested list (`ol` / `ul`
 * with a heading per path), so a screen reader walks it in the same order.
 * It scrolls inside its own box, never the page.
 */
export function FlowCanvas({
  definition,
  triggerType,
  stats,
  stepStats,
  onSelectStep,
}: {
  definition: FlowDefinition;
  triggerType: TriggerType;
  stats: AutomationStats;
  stepStats?: FlowStepStats | undefined;
  /** Called with the step id when a node is chosen (click, Enter or Space). */
  onSelectStep?: ((stepId: string) => void) | undefined;
}) {
  const layout = layoutFlow(definition);
  // The tree is centred and usually wider than the pane: start scrolled to the
  // middle so the trunk (trigger and first steps) is in view, not cut at the edge.
  const boxRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (box) box.scrollLeft = Math.max(0, (box.scrollWidth - box.clientWidth) / 2);
  }, [definition]);
  const shared: Shared = {
    steps: definition.steps,
    stepStats,
    currency: stats.currency,
    onSelectStep,
  };
  return (
    <div
      ref={boxRef}
      role="region"
      aria-label="Flow canvas"
      // A scroll box must be reachable by keyboard.
      tabIndex={0}
      className="overflow-auto max-h-[70vh] max-w-full rounded-xl border border-border-soft p-4"
    >
      <div className="min-w-max flex flex-col items-center">
        <div
          tabIndex={0}
          aria-label={`Trigger: ${TRIGGER_LABELS[triggerType]}. Enrolled ${formatNumber(stats.enrolled)}, active ${formatNumber(stats.active)}.`}
          className="w-56 rounded-xl border border-border bg-accent-soft px-3 py-2.5"
        >
          <span className="block text-xs text-muted font-medium">Trigger</span>
          <span className="block text-sm mt-0.5">{TRIGGER_LABELS[triggerType]}</span>
          <span className="flex gap-3 mt-1.5 text-xs text-muted font-mono">
            <span>Enrolled {formatNumber(stats.enrolled)}</span>
            <span>Active {formatNumber(stats.active)}</span>
          </span>
        </div>
        <Sequence items={layout.items} {...shared} />
        <Connector />
        <span className="text-xs text-muted">End of flow</span>
        {layout.orphans.length > 0 ? (
          <section className="mt-5 w-full" aria-labelledby="canvas-orphans-heading">
            <h3 id="canvas-orphans-heading" className="m-0 mb-2 text-xs font-semibold text-muted">
              Not reached by any path
            </h3>
            <ol className="m-0 p-0 list-none flex flex-col gap-2 items-center">
              {layout.orphans.map((o) => (
                <li key={o.step.id}>
                  <Node step={o.step} index={o.index} {...shared} />
                </li>
              ))}
            </ol>
          </section>
        ) : null}
      </div>
    </div>
  );
}
